import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Animated,
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { Ionicons, Octicons } from "@expo/vector-icons";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";

import Colors from "../../../Resources/Color";
import Fonts from "../../../Resources/Fonts";
import GlobalStyles from "../../../Resources/Styles";
import ActivePulseIndicator from "../../Block/shared/ActivePulseIndicator";
import CustomActivityIndicator from "../../Components/CustomActivityIndicator";
import HoverableTouchableOpacity from "../../Components/HoverableTouchableOpacity";
import IconEmptyView from "../../Components/IconEmptyView";
import LoadingLogo from "../../Components/LoadingLogo";
import Tooltip from "../../Components/Tooltip";
import { useToast } from "../../contexts/ToastContext";
import useChevronRotation from "../../hooks/useChevronRotation";
import { useDevice, useScreenDimensions } from "../../_layout";
import { useModalKeyboardShortcuts } from "../../platform/guards/modalKeyboardShortcuts";
import {
  fetchClientNutritionAssignments,
  saveClientNutritionAssignmentChanges,
} from "./nutritionPlanAssignments";
import { getNutritionPlanActionErrorDescription } from "./nutritionPlanActionSnapshot";
import { getNutritionPlanDisplayName } from "./nutritionPlanNameUtils";
import {
  buildAssignmentPreviewRecord,
  buildRenderablePlan,
  hasTrustedAssignmentPlanMeals,
  resolveAssignmentConflictPlanRecord,
} from "./nutritionPlanAssignmentModalUtils";
import {
  CLIENT_NUTRITION_STATUS_ACTIVE,
  CLIENT_NUTRITION_STATUS_ARCHIVED,
} from "../../Firestore/nutrition/nutritionPaths";
import {
  sortClientNutritionPlans,
} from "../../Firestore/nutrition/nutritionRecords";
import {
  applyClientAssignmentStageAction,
  deriveClientAssignmentSections,
  getClientAssignmentStage,
  getSelectedClientRowBadges,
  hasPendingAssignmentChanges,
  removeClientAssignmentStage,
} from "./nutritionPlanAssignmentState";
import {
  getCompactAssignmentReviewClientId,
  getCompactAssignmentStep,
  NUTRITION_PLAN_ASSIGNMENT_COMPACT_STEP_REVIEW,
  NUTRITION_PLAN_ASSIGNMENT_COMPACT_STEP_SELECT,
} from "./nutritionPlanAssignmentCompactFlow";
import { filterNutritionAssignmentEligibleClients } from "../../invitations/invitedClientUiGuards";
import PendingInvitationIndicator, {
  PENDING_INVITATION_INDICATOR_VARIANT,
} from "../../invitations/PendingInvitationIndicator";
import {
  ASSIGNMENT_CONFLICT_STATE_ACCEPTED,
  ASSIGNMENT_CONFLICT_STATE_REMOVED,
  ASSIGNMENT_CONFLICT_STATE_UNRESOLVED,
  getAssignmentConflictResolutionStatus,
  getNutritionConflictDescription,
  getNutritionConflictSummary,
  getUnresolvedAssignmentConflictClientIds,
} from "../shared/nutritionConflictWarnings";

function hasText(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function getClientDisplayName(client) {
  return [client?.nameForCoach, client?.surnameForCoach].filter(hasText).join(" ").trim();
}

function getClientPlansTitle(client, t) {
  const displayName = getClientDisplayName(client);
  if (!displayName) {
    return "";
  }

  return t("nutritionPlanAssignmentsAthletePlansTitle-string", {
    defaultValue: "{{name}}'s plans",
    name: displayName,
  });
}

function matchesClientSearch(client, searchText) {
  const normalizedSearch = String(searchText ?? "").trim().toLowerCase();
  if (!normalizedSearch) {
    return true;
  }

  return getClientDisplayName(client).toLowerCase().includes(normalizedSearch);
}

function normalizeClientId(value) {
  return String(value ?? "").trim().toLowerCase();
}

export const NutritionPlanAssignmentModal = observer(function NutritionPlanAssignmentModal({
  isVisible,
  coach,
  plan,
  onClose,
}) {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const { screenSize } = useScreenDimensions();
  const { isMobile, isIOS } = useDevice();
  const isCompactLayout = screenSize === "small";
  const language = i18n?.language ?? "en";
  const searchInputRef = useRef(null);
  const searchInputPressingRef = useRef(false);
  const [searchText, setSearchText] = useState("");
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [focusedClientId, setFocusedClientId] = useState(null);
  const [assignmentsByClientId, setAssignmentsByClientId] = useState({});
  const [loadingClientIds, setLoadingClientIds] = useState({});
  const [loadErrorsByClientId, setLoadErrorsByClientId] = useState({});
  const [stagedChangesByClientId, setStagedChangesByClientId] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isArchivedExpanded, setIsArchivedExpanded] = useState(false);
  const [isCloseHovered, setIsCloseHovered] = useState(false);
  const [isSearchHovered, setIsSearchHovered] = useState(false);
  const [hoveredClientRowId, setHoveredClientRowId] = useState(null);
  const [hoveredSelectedClientId, setHoveredSelectedClientId] = useState(null);
  const [hoveredSelectedClientRemoveId, setHoveredSelectedClientRemoveId] = useState(null);
  const [hoveredPlanActionKey, setHoveredPlanActionKey] = useState(null);
  const [isCancelHovered, setIsCancelHovered] = useState(false);
  const [isSaveHovered, setIsSaveHovered] = useState(false);
  const archivedChevronRotation = useChevronRotation(isArchivedExpanded);
  const [conflictResolutionByClientId, setConflictResolutionByClientId] = useState({});
  const [compactStep, setCompactStep] = useState(
    NUTRITION_PLAN_ASSIGNMENT_COMPACT_STEP_SELECT,
  );
  const [renderPlan, setRenderPlan] = useState(plan ?? null);
  const [conflictPlanRecord, setConflictPlanRecord] = useState(null);
  const [isConflictPlanReady, setIsConflictPlanReady] = useState(false);
  const displayPlan = renderPlan ?? plan ?? null;

  const assignmentEligibleClients = useMemo(() => {
    return filterNutritionAssignmentEligibleClients(
      Array.isArray(coach?.clients) ? coach.clients : coach?.activeClients ?? [],
    ).sort((left, right) =>
      getClientDisplayName(left).localeCompare(getClientDisplayName(right), undefined, {
        sensitivity: "base",
      }),
    );
  }, [coach?.activeClients, coach?.clients]);

  const clientsById = useMemo(
    () =>
      new Map(
        assignmentEligibleClients.map((client) => [
          String(client?.id ?? "").trim().toLowerCase(),
          client,
        ]),
      ),
    [assignmentEligibleClients],
  );

  const filteredClients = useMemo(
    () => assignmentEligibleClients.filter((client) => matchesClientSearch(client, searchText)),
    [assignmentEligibleClients, searchText],
  );

  const focusedClient = focusedClientId ? clientsById.get(focusedClientId) ?? null : null;
  const focusedAssignments = focusedClientId
    ? assignmentsByClientId[focusedClientId] ?? []
    : [];
  const focusedStagedChanges = useMemo(
    () => getClientAssignmentStage(stagedChangesByClientId, focusedClientId),
    [focusedClientId, stagedChangesByClientId],
  );
  const { active: activeAssignments, archived: archivedAssignments } = useMemo(
    () => deriveClientAssignmentSections(focusedAssignments, focusedStagedChanges),
    [focusedAssignments, focusedStagedChanges],
  );
  const activeAssignmentsWithPreview = useMemo(() => {
    if (!focusedClient || !displayPlan) {
      return activeAssignments;
    }

    return [
      buildAssignmentPreviewRecord(displayPlan),
      ...activeAssignments,
    ];
  }, [activeAssignments, displayPlan, focusedClient]);
  const conflictSummariesByClientId = useMemo(() => {
    if (!isConflictPlanReady || !conflictPlanRecord) {
      return {};
    }

    return assignmentEligibleClients.reduce((nextSummaries, client) => {
      const clientId = normalizeClientId(client?.id);
      if (!clientId) {
        return nextSummaries;
      }

      nextSummaries[clientId] = getNutritionConflictSummary({
        client,
        source: conflictPlanRecord,
        t,
      });
      return nextSummaries;
    }, {});
  }, [assignmentEligibleClients, conflictPlanRecord, isConflictPlanReady, t]);
  const focusedConflictSummary = focusedClientId
    ? conflictSummariesByClientId[focusedClientId] ?? { hasConflicts: false, reasons: [] }
    : { hasConflicts: false, reasons: [] };
  const focusedConflictResolutionState = getAssignmentConflictResolutionStatus({
    clientId: focusedClientId,
    selectedClientIds,
    acceptedConflictClientIds: conflictResolutionByClientId,
    conflictSummariesByClientId,
  });
  const canSave = hasPendingAssignmentChanges({
    selectedClientIds,
    stagedChangesByClientId,
  }) && !isSaving;
  const isBusy = isSaving;
  const shouldShowPlansLoadingLogo = loadingClientIds[focusedClientId];
  const resolvedCompactStep = getCompactAssignmentStep({
    isCompactLayout,
    currentStep: compactStep,
    selectedClientIds,
  });
  const isCompactSelectionStep =
    isCompactLayout &&
    resolvedCompactStep === NUTRITION_PLAN_ASSIGNMENT_COMPACT_STEP_SELECT;
  const isCompactReviewStep =
    isCompactLayout &&
    resolvedCompactStep === NUTRITION_PLAN_ASSIGNMENT_COMPACT_STEP_REVIEW;

  const resetModalState = useCallback(() => {
    setSearchText("");
    setSelectedClientIds([]);
    setFocusedClientId(null);
    setAssignmentsByClientId({});
    setLoadingClientIds({});
    setLoadErrorsByClientId({});
    setStagedChangesByClientId({});
    setIsSaving(false);
    setIsArchivedExpanded(false);
    setIsSearchHovered(false);
    setHoveredClientRowId(null);
    setHoveredSelectedClientId(null);
    setHoveredSelectedClientRemoveId(null);
    setHoveredPlanActionKey(null);
    setIsCancelHovered(false);
    setIsSaveHovered(false);
    setConflictResolutionByClientId({});
    setCompactStep(NUTRITION_PLAN_ASSIGNMENT_COMPACT_STEP_SELECT);
    setConflictPlanRecord(null);
    setIsConflictPlanReady(false);
  }, []);

  useEffect(() => {
    if (!isVisible) {
      resetModalState();
    }
  }, [isVisible, plan?.id, resetModalState]);

  useEffect(() => {
    if (!isVisible || isMobile) {
      return undefined;
    }

    const focusSearchInput = () => {
      searchInputRef.current?.focus?.();
    };

    if (typeof requestAnimationFrame === "function") {
      const rafId = requestAnimationFrame(focusSearchInput);

      return () => {
        cancelAnimationFrame(rafId);
      };
    }

    const timeoutId = setTimeout(focusSearchInput, 0);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isMobile, isVisible]);

  useEffect(() => {
    let isActive = true;

    async function hydrateRenderPlan() {
      if (!plan) {
        setRenderPlan(null);
        return;
      }

      const normalizedPlanId = normalizeClientId(plan?.id);
      const cachedRootRecord =
        coach?.nutritionStore?.planRootRecordsById?.get?.(normalizedPlanId) ?? null;
      const basePlan = buildRenderablePlan(plan, null, cachedRootRecord);
      setRenderPlan(basePlan);

      if (!coach?.nutritionStore || !normalizedPlanId || hasTrustedAssignmentPlanMeals(plan)) {
        return;
      }

      try {
        const mealRecords = await coach.nutritionStore.loadPlanMealRecords(normalizedPlanId);
        if (!isActive) {
          return;
        }

        setRenderPlan(buildRenderablePlan(plan, mealRecords, cachedRootRecord));
      } catch (error) {
        if (!isActive) {
          return;
        }

        setRenderPlan(basePlan);
      }
    }

    hydrateRenderPlan();

    return () => {
      isActive = false;
    };
  }, [coach?.nutritionStore, plan]);

  useEffect(() => {
    let isActive = true;

    async function hydrateConflictPlanRecord() {
      if (!isVisible || !plan) {
        setConflictPlanRecord(null);
        setIsConflictPlanReady(false);
        return;
      }

      setConflictPlanRecord(null);
      setIsConflictPlanReady(false);

      try {
        const { planRecord } = await resolveAssignmentConflictPlanRecord({
          plan,
          nutritionStore: coach?.nutritionStore,
          language,
        });
        if (!isActive) {
          return;
        }

        setConflictPlanRecord(planRecord);
        setIsConflictPlanReady(true);
      } catch (error) {
        if (!isActive) {
          return;
        }

        setConflictPlanRecord(null);
        setIsConflictPlanReady(false);
      }
    }

    hydrateConflictPlanRecord();

    return () => {
      isActive = false;
    };
  }, [coach?.nutritionStore, isVisible, language, plan]);

  useEffect(() => {
    setIsArchivedExpanded(false);
  }, [focusedClientId]);

  useEffect(() => {
    if (compactStep !== resolvedCompactStep) {
      setCompactStep(resolvedCompactStep);
    }
  }, [compactStep, resolvedCompactStep]);

  const ensureAssignmentsLoaded = useCallback(
    async (clientId) => {
      const normalizedClientId = String(clientId ?? "").trim().toLowerCase();
      if (!normalizedClientId) {
        return;
      }

      if (assignmentsByClientId[normalizedClientId] || loadingClientIds[normalizedClientId]) {
        return;
      }

      const clientRecord = clientsById.get(normalizedClientId);
      if (!clientRecord) {
        return;
      }

      setLoadingClientIds((prev) => ({
        ...prev,
        [normalizedClientId]: true,
      }));

      try {
        const assignments = await fetchClientNutritionAssignments(clientRecord);
        setAssignmentsByClientId((prev) => ({
          ...prev,
          [normalizedClientId]: assignments,
        }));
        setLoadErrorsByClientId((prev) => ({
          ...prev,
          [normalizedClientId]: null,
        }));
      } catch (error) {
        console.error("Failed to load client nutrition assignments:", error);
        setLoadErrorsByClientId((prev) => ({
          ...prev,
          [normalizedClientId]: true,
        }));
        showToast("nutritionPlanAssignmentsLoadErrorTitle-string", {
          type: "error",
          description: "nutritionPlanAssignmentsLoadErrorDescription-string",
        });
      } finally {
        setLoadingClientIds((prev) => {
          const nextState = { ...prev };
          delete nextState[normalizedClientId];
          return nextState;
        });
      }
    },
    [assignmentsByClientId, clientsById, loadingClientIds, showToast],
  );

  useEffect(() => {
    if (isVisible && focusedClientId) {
      ensureAssignmentsLoaded(focusedClientId);
    }
  }, [ensureAssignmentsLoaded, focusedClientId, isVisible]);

  const handleClose = useCallback(() => {
    if (isBusy) {
      return;
    }

    onClose?.();
  }, [isBusy, onClose]);

  const toggleClientSelection = useCallback(
    (client) => {
      const normalizedClientId = normalizeClientId(client?.id);
      if (!normalizedClientId) {
        return;
      }

      setSelectedClientIds((prevSelected) => {
        if (prevSelected.includes(normalizedClientId)) {
          const nextSelected = prevSelected.filter((clientId) => clientId !== normalizedClientId);
          setConflictResolutionByClientId((prevState) => ({
            ...prevState,
            [normalizedClientId]: ASSIGNMENT_CONFLICT_STATE_REMOVED,
          }));
          setStagedChangesByClientId((prevChanges) =>
            removeClientAssignmentStage(prevChanges, normalizedClientId),
          );
          setFocusedClientId((currentFocusedClientId) => {
            if (currentFocusedClientId !== normalizedClientId) {
              return currentFocusedClientId;
            }

            return nextSelected[0] ?? null;
          });
          return nextSelected;
        }

        ensureAssignmentsLoaded(normalizedClientId);
        setConflictResolutionByClientId((prevState) => {
          const nextState = { ...prevState };
          delete nextState[normalizedClientId];
          return nextState;
        });
        setFocusedClientId((currentFocusedClientId) => currentFocusedClientId ?? normalizedClientId);
        return [...prevSelected, normalizedClientId];
      });
    },
    [ensureAssignmentsLoaded],
  );

  const removeSelectedClient = useCallback((clientId) => {
    const normalizedClientId = normalizeClientId(clientId);
    if (!normalizedClientId) {
      return;
    }

    setConflictResolutionByClientId((prevState) => ({
      ...prevState,
      [normalizedClientId]: ASSIGNMENT_CONFLICT_STATE_REMOVED,
    }));
    setStagedChangesByClientId((prevChanges) =>
      removeClientAssignmentStage(prevChanges, normalizedClientId),
    );
    setSelectedClientIds((prevSelected) => {
      const nextSelected = prevSelected.filter((selectedId) => selectedId !== normalizedClientId);
      setFocusedClientId((currentFocusedClientId) => {
        if (currentFocusedClientId !== normalizedClientId) {
          return currentFocusedClientId;
        }

        return nextSelected[0] ?? null;
      });
      return nextSelected;
    });
  }, []);

  const acceptFocusedClientConflict = useCallback(() => {
    if (!focusedClientId) {
      return;
    }

    setConflictResolutionByClientId((prevState) => ({
      ...prevState,
      [focusedClientId]: ASSIGNMENT_CONFLICT_STATE_ACCEPTED,
    }));
  }, [focusedClientId]);

  const stageAssignmentAction = useCallback((assignment, action) => {
    if (!focusedClientId || !assignment?.client_plan_id) {
      return;
    }

    setStagedChangesByClientId((prevChanges) =>
      applyClientAssignmentStageAction({
        stagedChangesByClientId: prevChanges,
        clientId: focusedClientId,
        assignment,
        action,
      }),
    );
  }, [focusedClientId]);

  const handleSave = useCallback(async () => {
    if (!canSave || !plan) {
      return;
    }

    let planSource = conflictPlanRecord;
    if (!planSource) {
      try {
        const { planRecord } = await resolveAssignmentConflictPlanRecord({
          plan,
          nutritionStore: coach?.nutritionStore,
          language,
        });
        planSource = planRecord;
        setConflictPlanRecord(planRecord);
        setIsConflictPlanReady(true);
      } catch (error) {
        showToast("nutritionPlanAssignmentsSaveErrorTitle-string", {
          type: "error",
          title: "nutritionPlanAssignmentsSaveErrorTitle-string",
          description: getNutritionPlanActionErrorDescription(
            error,
            t("nutritionCommonTryAgainLater-string", "Try again in a moment."),
            t,
          ),
        });
        return;
      }
    }

    const currentConflictSummariesByClientId = assignmentEligibleClients.reduce(
      (nextSummaries, clientRecord) => {
        const clientId = normalizeClientId(clientRecord?.id);
        if (!clientId) {
          return nextSummaries;
        }

        nextSummaries[clientId] = getNutritionConflictSummary({
          client: clientRecord,
          source: planSource,
          t,
        });
        return nextSummaries;
      },
      {},
    );

    const unresolvedClientIds = getUnresolvedAssignmentConflictClientIds({
      selectedClientIds,
      acceptedConflictClientIds: conflictResolutionByClientId,
      conflictSummariesByClientId: currentConflictSummariesByClientId,
    });

    if (unresolvedClientIds.length > 0) {
      const firstUnresolvedClientId = unresolvedClientIds[0];
      setFocusedClientId(firstUnresolvedClientId);
      ensureAssignmentsLoaded(firstUnresolvedClientId);
      showToast("nutritionPlanAssignmentsResolveConflictsTitle-string", {
        type: "warning",
        title: "nutritionPlanAssignmentsResolveConflictsTitle-string",
        description:
          unresolvedClientIds.length === 1
            ? "nutritionPlanAssignmentsResolveConflictsSingleDescription-string"
            : "nutritionPlanAssignmentsResolveConflictsMultipleDescription-string",
      });
      return;
    }

    setIsSaving(true);

    try {
      const saveResults = await Promise.allSettled(
        selectedClientIds.map(async (clientId) => {
          try {
            const client = clientsById.get(clientId);
            if (!client) {
              throw new Error("Missing selected client");
            }

            const stage = getClientAssignmentStage(stagedChangesByClientId, clientId);
            const saveResult = await saveClientNutritionAssignmentChanges({
              coach,
              client,
              plan,
              publishPlan: true,
              archiveClientPlanIds: stage.archiveClientPlanIds,
              restoreClientPlanIds: stage.restoreClientPlanIds,
            });

            return {
              clientId,
              ...saveResult,
            };
          } catch (error) {
            throw {
              clientId,
              error,
            };
          }
        }),
      );

      const successfulSaves = saveResults
        .filter((result) => result.status === "fulfilled")
        .map((result) => result.value);
      const failedSaves = saveResults.filter((result) => result.status === "rejected");
      const successfulClientIdSet = new Set(successfulSaves.map(({ clientId }) => clientId));
      const failedClientIdSet = new Set(
        failedSaves
          .map((result) => String(result.reason?.clientId ?? "").trim().toLowerCase())
          .filter(Boolean),
      );

      if (successfulSaves.length > 0) {
        setAssignmentsByClientId((prev) => {
          const nextAssignments = { ...prev };

          successfulSaves.forEach(
            ({ clientId, rootRecord, archivedClientPlanIds, restoredClientPlanIds, updatedAt }) => {
              const archiveIdSet = new Set(archivedClientPlanIds);
              const restoreIdSet = new Set(restoredClientPlanIds);
              const currentAssignments = (nextAssignments[clientId] ?? []).map((assignment) => {
                const assignmentId = String(assignment?.client_plan_id ?? "").trim().toLowerCase();
                if (archiveIdSet.has(assignmentId)) {
                  return {
                    ...assignment,
                    status: CLIENT_NUTRITION_STATUS_ARCHIVED,
                    deactivated_at: updatedAt,
                  };
                }

                if (restoreIdSet.has(assignmentId)) {
                  return {
                    ...assignment,
                    status: CLIENT_NUTRITION_STATUS_ACTIVE,
                    activated_at: updatedAt,
                    deactivated_at: undefined,
                  };
                }

                return assignment;
              });

              nextAssignments[clientId] = sortClientNutritionPlans(
                rootRecord ? [rootRecord, ...currentAssignments] : currentAssignments,
              );
            },
          );

          return nextAssignments;
        });
        setSelectedClientIds((prevSelected) =>
          prevSelected.filter((clientId) => failedClientIdSet.has(clientId)),
        );
        setStagedChangesByClientId((prevChanges) => {
          let nextChanges = { ...prevChanges };

          successfulSaves.forEach(({ clientId }) => {
            nextChanges = removeClientAssignmentStage(nextChanges, clientId);
          });

          return nextChanges;
        });
        setFocusedClientId((currentFocusedClientId) => {
          if (!currentFocusedClientId || !successfulClientIdSet.has(currentFocusedClientId)) {
            return currentFocusedClientId;
          }

          const nextSelectedClientId = selectedClientIds.find((clientId) =>
            failedClientIdSet.has(clientId),
          );
          return nextSelectedClientId ?? null;
        });
      }

      if (failedSaves.length === 0) {
        showToast("nutritionPlanAssignmentsSaveSuccessTitle-string", {
          title: "nutritionPlanAssignmentsSaveSuccessTitle-string",
          description: "nutritionPlanAssignmentsSaveSuccessDescription-string",
        });
        onClose?.();
        return;
      }

      if (successfulSaves.length > 0) {
        const firstFailureError = failedSaves[0]?.reason?.error ?? null;
        const failureDescription = getNutritionPlanActionErrorDescription(
          firstFailureError,
          "",
          t,
        );
        showToast("nutritionPlanAssignmentsSavePartialTitle-string", {
          type: "warning",
          title: "nutritionPlanAssignmentsSavePartialTitle-string",
          description: failureDescription
            ? "nutritionPlanAssignmentsSavePartialDescriptionWithError-string"
            : "nutritionPlanAssignmentsSavePartialDescription-string",
          variables: {
            successCount: successfulSaves.length,
            failureCount: failedSaves.length,
            errorDescription: failureDescription,
          },
        });
        return;
      }

      const firstFailureError = failedSaves[0]?.reason?.error ?? null;
      showToast("nutritionPlanAssignmentsSaveErrorTitle-string", {
        type: "error",
        title: "nutritionPlanAssignmentsSaveErrorTitle-string",
        description: getNutritionPlanActionErrorDescription(
          firstFailureError,
          t("nutritionCommonTryAgainLater-string", "Try again in a moment."),
          t,
        ),
      });
    } catch (error) {
      console.error("Failed to save nutrition plan changes:", error);
      showToast("nutritionPlanAssignmentsSaveErrorTitle-string", {
        type: "error",
        title: "nutritionPlanAssignmentsSaveErrorTitle-string",
        description: getNutritionPlanActionErrorDescription(
          error,
          t("nutritionCommonTryAgainLater-string", "Try again in a moment."),
          t,
        ),
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    assignmentEligibleClients,
    canSave,
    conflictResolutionByClientId,
    clientsById,
    coach,
    conflictPlanRecord,
    ensureAssignmentsLoaded,
    language,
    onClose,
    plan,
    selectedClientIds,
    showToast,
    stagedChangesByClientId,
    t,
  ]);

  const handleOpenCompactReview = useCallback(() => {
    if (!isCompactLayout || selectedClientIds.length === 0) {
      return;
    }

    const reviewClientId = getCompactAssignmentReviewClientId({
      focusedClientId,
      selectedClientIds,
    });

    if (reviewClientId) {
      setFocusedClientId(reviewClientId);
      ensureAssignmentsLoaded(reviewClientId);
    }

    setCompactStep(NUTRITION_PLAN_ASSIGNMENT_COMPACT_STEP_REVIEW);
  }, [ensureAssignmentsLoaded, focusedClientId, isCompactLayout, selectedClientIds]);

  const handleCompactBack = useCallback(() => {
    setCompactStep(NUTRITION_PLAN_ASSIGNMENT_COMPACT_STEP_SELECT);
  }, []);

  useModalKeyboardShortcuts({
    isVisible,
    onConfirm: isCompactSelectionStep ? handleOpenCompactReview : handleSave,
    onCancel: isCompactReviewStep ? handleCompactBack : handleClose,
    isConfirmEnabled: isCompactSelectionStep ? selectedClientIds.length > 0 : canSave,
    allowEnterOnSingleLineInput: false,
    isBlocked: () => isBusy,
  });

  const primaryButtonLabel = isCompactLayout
    ? t("nutritionPlanAssignmentsSaveCompact-string", "Save")
    : t("nutritionPlanAssignmentsSaveChanges-string", "Save changes");
  const compactReviewButtonLabel = t(
    "nutritionPlanAssignmentsReviewSelectedCompact-string",
    "Review ({{count}})",
    { count: selectedClientIds.length },
  );
  const secondaryButtonLabel = isCompactReviewStep
    ? t("back-string")
    : t("cancel-string");

  const renderSelectionPanel = ({ allowPanelScroll = false } = {}) => (
    <View
      style={[
        styles.panel,
        styles.selectionPanel,
        isCompactLayout ? styles.panelCompact : null,
        allowPanelScroll ? styles.panelCompactStep : null,
      ]}
    >
      <View
        style={[
          styles.panelTopBlock,
          isCompactLayout ? styles.panelTopBlockCompact : null,
          !isCompactLayout ? styles.selectionPanelTopBlockDesktop : null,
        ]}
      >
        <View
          style={[
            styles.panelHeader,
            !isCompactLayout ? styles.selectionPanelHeaderDesktop : null,
          ]}
        >
          <Text style={styles.panelTitle}>
            {t("nutritionPlanAssignmentsAthletesTitle-string", "Select athletes")}
          </Text>
        </View>

        <TouchableOpacity
          activeOpacity={1}
          onPress={() => {
            if (!searchInputPressingRef.current) {
              searchInputRef.current?.focus?.();
            }
          }}
          onMouseEnter={!isMobile ? () => setIsSearchHovered(true) : undefined}
          onMouseLeave={() => setIsSearchHovered(false)}
          style={[
            styles.searchField,
            isSearchHovered ? styles.searchFieldHovered : null,
          ]}
        >
          <Octicons name="search" size={14} color={Colors.grayColor} />
          <TextInput
            ref={searchInputRef}
            value={searchText}
            onChangeText={setSearchText}
            onPressIn={() => {
              searchInputPressingRef.current = true;
            }}
            onPressOut={() => {
              searchInputPressingRef.current = false;
            }}
            onBlur={() => {
              searchInputPressingRef.current = false;
            }}
            placeholder={t(
              "nutritionPlanAssignmentsSearchPlaceholder-string",
              "Search athletes",
            )}
            placeholderTextColor={Colors.grayColor}
            style={[
              styles.searchInput,
              isMobile && isIOS ? styles.searchInputIOS : null,
            ]}
            accessibilityLabel={t(
              "nutritionPlanAssignmentsSearchPlaceholder-string",
              "Search athletes",
            )}
            aria-label={t(
              "nutritionPlanAssignmentsSearchPlaceholder-string",
              "Search athletes",
            )}
          />
        </TouchableOpacity>
      </View>

      <ScrollView
        style={[
          styles.panelScroll,
          !isCompactLayout ? styles.panelScrollDesktop : null,
          styles.selectionPanelScroll,
          isCompactLayout ? styles.panelScrollCompact : null,
        ]}
        contentContainerStyle={[
          styles.panelScrollContent,
          isCompactLayout ? styles.selectionPanelScrollContentCompact : null,
          !isCompactLayout ? styles.selectionPanelScrollContentDesktop : null,
        ]}
        showsVerticalScrollIndicator={!isCompactLayout || allowPanelScroll}
        scrollEnabled={!isCompactLayout || allowPanelScroll}
      >
        {filteredClients.length === 0 ? (
          <View style={styles.searchEmptyState}>
            <Text style={styles.searchEmptyStateText}>
              {t(
                "nutritionPlanAssignmentsNoAthletesFound-string",
                "No athletes match the current search.",
              )}
            </Text>
          </View>
        ) : (
          filteredClients.map((client) => {
            const clientId = normalizeClientId(client?.id);
            const isSelected = selectedClientIds.includes(clientId);
            const isHovered = hoveredClientRowId === clientId;
            const conflictSummary =
              conflictSummariesByClientId[clientId] ?? { hasConflicts: false, reasons: [] };
            const conflictTooltip = getNutritionConflictDescription(conflictSummary);

            return (
              <TouchableOpacity
                key={clientId}
                activeOpacity={0.85}
                onPress={() => toggleClientSelection(client)}
                onMouseEnter={!isMobile ? () => setHoveredClientRowId(clientId) : undefined}
                onMouseLeave={() => setHoveredClientRowId(null)}
              >
                <View
                  style={[
                    styles.clientRow,
                    isSelected ? styles.clientRowSelected : null,
                    isHovered ? styles.clientRowHovered : null,
                    isSelected && isHovered ? styles.clientRowSelectedHovered : null,
                  ]}
                >
                  <View style={styles.clientRowCheckbox}>
                    <View
                      style={[
                        styles.checkbox,
                        isSelected ? styles.checkboxSelected : null,
                      ]}
                    >
                      {isSelected ? (
                        <Ionicons name="checkmark" size={12} color={Colors.whiteColor} />
                      ) : null}
                    </View>
                  </View>
                    <View style={styles.clientRowText}>
                      <View style={styles.clientNameRow}>
                        <Text numberOfLines={1} style={styles.clientName}>
                          {getClientDisplayName(client)}
                        </Text>
                        {conflictSummary.hasConflicts ? (
                          <Tooltip content={conflictTooltip} placement="top" rawText={true}>
                            <View style={styles.clientConflictIconWrap}>
                              <Ionicons
                                name="warning-outline"
                                size={14}
                                color={Colors.warningTextColor}
                              />
                            </View>
                          </Tooltip>
                        ) : null}
                        <PendingInvitationIndicator
                          client={client}
                          variant={PENDING_INVITATION_INDICATOR_VARIANT.ICON_ONLY}
                      />
                    </View>
                  </View>
                </View>
              </TouchableOpacity>
            );
          })
        )}
      </ScrollView>
    </View>
  );

  const renderCurrentPlansPanel = ({ allowPanelScroll = false } = {}) => (
    <View
      style={[
        styles.panel,
        styles.currentPlansPanel,
        isCompactLayout ? styles.panelCompact : null,
        allowPanelScroll ? styles.panelCompactStep : null,
      ]}
    >
      <ScrollView
        style={[
          styles.panelScroll,
          !isCompactLayout ? styles.panelScrollDesktop : null,
          isCompactLayout ? styles.panelScrollCompact : null,
        ]}
        contentContainerStyle={[
          styles.panelScrollContent,
          isCompactLayout ? styles.currentPlansPanelScrollContentCompact : null,
          !isCompactLayout ? styles.currentPlansPanelScrollContentDesktop : null,
        ]}
        showsVerticalScrollIndicator={!isCompactLayout || allowPanelScroll}
        scrollEnabled={!isCompactLayout || allowPanelScroll}
      >
        <View style={styles.sectionHeaderRow}>
          <View style={styles.sectionHeaderLead}>
            <Text style={styles.sectionTitle}>
              {t("nutritionPlanAssignmentsSelectedAthletes-string", "Selected")}
            </Text>
            <Text style={styles.sectionCount}>{selectedClientIds.length}</Text>
          </View>
        </View>

        {selectedClientIds.length > 0 ? (
          <View
            style={[
              styles.selectedClientList,
              isCompactLayout ? styles.selectedClientListCompact : null,
            ]}
          >
            {selectedClientIds.map((clientId) => {
              const client = clientsById.get(clientId);
              if (!client) {
                return null;
              }

              const isFocused = focusedClientId === clientId;
              const isHovered = hoveredSelectedClientId === clientId;
              const { isReady: isBadgeReady, addCount, removalCount } = getSelectedClientRowBadges({
                selectedClientIds,
                clientId,
                stagedChangesByClientId,
                assignments: assignmentsByClientId[clientId],
              });
              const isRemoveHovered = hoveredSelectedClientRemoveId === clientId;
              const conflictSummary =
                conflictSummariesByClientId[clientId] ?? { hasConflicts: false, reasons: [] };
              const conflictResolutionState = getAssignmentConflictResolutionStatus({
                clientId,
                selectedClientIds,
                acceptedConflictClientIds: conflictResolutionByClientId,
                conflictSummariesByClientId,
              });
              const hasUnresolvedConflict =
                conflictResolutionState === ASSIGNMENT_CONFLICT_STATE_UNRESOLVED;

              return (
                <View
                  key={clientId}
                  style={[
                    styles.selectedClientRow,
                    isCompactLayout ? styles.selectedClientRowCompact : null,
                    hasUnresolvedConflict ? styles.selectedClientRowConflict : null,
                    isHovered ? styles.selectedClientRowHovered : null,
                    isFocused ? styles.selectedClientRowFocused : null,
                    isFocused && isHovered ? styles.selectedClientRowFocusedHovered : null,
                  ]}
                  onMouseEnter={!isMobile ? () => setHoveredSelectedClientId(clientId) : undefined}
                  onMouseLeave={() => setHoveredSelectedClientId(null)}
                >
                  <TouchableOpacity
                    activeOpacity={0.85}
                    onPress={() => {
                      setFocusedClientId(clientId);
                      ensureAssignmentsLoaded(clientId);
                    }}
                    style={styles.selectedClientMainButton}
                  >
                    <View style={styles.selectedClientIdentity}>
                      <View style={styles.selectedClientIdentityContent}>
                        <View style={styles.selectedClientNameRow}>
                          <Text
                            numberOfLines={1}
                            style={[
                              styles.selectedClientName,
                              isFocused ? styles.selectedClientNameFocused : null,
                            ]}
                          >
                            {getClientDisplayName(client)}
                          </Text>
                          {hasUnresolvedConflict ? (
                            <Ionicons
                              name="warning-outline"
                              size={14}
                              color={Colors.warningTextColor}
                            />
                          ) : null}
                        </View>
                        {hasUnresolvedConflict ? (
                          <Text numberOfLines={2} style={styles.selectedClientConflictText}>
                            {getNutritionConflictDescription(conflictSummary)}
                          </Text>
                        ) : null}
                        {!isBadgeReady ? (
                          <View style={styles.selectedClientBadgeRowPlaceholder} />
                        ) : (addCount > 0 || removalCount > 0) ? (
                          <View style={styles.selectedClientBadgeRow}>
                            {addCount > 0 ? (
                              <View
                                style={[
                                  styles.selectedClientDeltaBadge,
                                  styles.selectedClientDeltaBadgeAdd,
                                ]}
                              >
                                <Text style={styles.selectedClientDeltaBadgeText}>+{addCount}</Text>
                              </View>
                            ) : null}
                            {removalCount > 0 ? (
                              <View
                                style={[
                                  styles.selectedClientDeltaBadge,
                                  styles.selectedClientDeltaBadgeRemove,
                                ]}
                              >
                                <Text
                                  style={[
                                    styles.selectedClientDeltaBadgeText,
                                    styles.selectedClientDeltaBadgeTextRemove,
                                  ]}
                                >
                                  -{removalCount}
                                </Text>
                              </View>
                            ) : null}
                          </View>
                        ) : null}
                      </View>
                    </View>
                  </TouchableOpacity>
                  <TouchableOpacity
                    activeOpacity={0.8}
                    onPress={() => removeSelectedClient(clientId)}
                    style={styles.selectedClientRemoveButton}
                    onMouseEnter={
                      !isMobile ? () => setHoveredSelectedClientRemoveId(clientId) : undefined
                    }
                    onMouseLeave={() => setHoveredSelectedClientRemoveId(null)}
                  >
                    <Ionicons
                      name="close-outline"
                      size={16}
                      color={isRemoveHovered ? Colors.darkgrayColor : Colors.grayColor}
                    />
                  </TouchableOpacity>
                </View>
              );
            })}
          </View>
        ) : null}

        {focusedClient ? (
          <View style={styles.panelHeader}>
            <Text numberOfLines={1} style={styles.panelTitle}>
              {getClientPlansTitle(focusedClient, t)}
            </Text>
          </View>
        ) : null}

        {!focusedClient ? (
          <View style={styles.emptyState}>
            <IconEmptyView
              iconLibrary="Ionicons"
              iconName="people-outline"
              mainText="nutritionPlanAssignmentsNoAthleteSelectedTitle-string"
              subText="nutritionPlanAssignmentsNoAthleteSelectedDescription-string"
              marginBottom={8}
            />
          </View>
        ) : shouldShowPlansLoadingLogo ? (
          <View style={styles.loadingState}>
            <LoadingLogo container={false} width={58} height={58} dark={true} />
          </View>
        ) : loadErrorsByClientId[focusedClientId] ? (
          <View style={styles.emptyState}>
            <Ionicons name="alert-circle-outline" size={18} color={Colors.warningTextColor} />
            <Text style={styles.emptyStateTitle}>
              {t(
                "nutritionPlanAssignmentsLoadErrorInlineTitle-string",
                "Could not load plans",
              )}
            </Text>
            <Text style={styles.emptyStateCopy}>
              {t(
                "nutritionPlanAssignmentsLoadErrorInlineDescription-string",
                "Try another athlete or close and reopen the modal.",
              )}
            </Text>
          </View>
        ) : (
          <>
            {focusedClient &&
            selectedClientIds.includes(focusedClientId) &&
            focusedConflictSummary.hasConflicts &&
            focusedConflictResolutionState === ASSIGNMENT_CONFLICT_STATE_UNRESOLVED ? (
              <View style={styles.conflictCallout}>
                <View style={styles.conflictCalloutHeader}>
                  <View style={styles.conflictCalloutTitleWrap}>
                    <Ionicons
                      name="warning-outline"
                      size={15}
                      color={Colors.warningTextColor}
                    />
                    <Text style={styles.conflictCalloutTitle}>
                      {t("nutritionPlanAssignmentConflictsTitle-string")}
                    </Text>
                  </View>
                </View>
                <View style={styles.conflictReasonList}>
                  {focusedConflictSummary.reasons.map((reason) => (
                    <View key={reason} style={styles.conflictReasonChip}>
                      <Text style={styles.conflictReasonChipText}>{reason}</Text>
                    </View>
                  ))}
                </View>
                <View style={styles.conflictCalloutActions}>
                  <HoverableTouchableOpacity
                    activeOpacity={0.85}
                    onPress={() => removeSelectedClient(focusedClientId)}
                    style={styles.conflictRemoveButton}
                    hoverStyle={styles.conflictRemoveButtonHover}
                  >
                    <Text style={styles.conflictRemoveButtonText}>
                      {t("nutritionPlanAssignmentRemoveAction-string")}
                    </Text>
                  </HoverableTouchableOpacity>
                  <HoverableTouchableOpacity
                    activeOpacity={0.85}
                    onPress={acceptFocusedClientConflict}
                    style={styles.conflictAcceptButton}
                    hoverStyle={styles.conflictAcceptButtonHover}
                  >
                    <Text style={styles.conflictAcceptButtonText}>
                      {t("nutritionPlanAssignmentAcceptAction-string")}
                    </Text>
                  </HoverableTouchableOpacity>
                </View>
              </View>
            ) : null}

            <View style={styles.sectionHeaderRow}>
              <View style={styles.sectionHeaderLead}>
                <ActivePulseIndicator />
                <Text style={styles.sectionTitle}>
                  {t("nutritionPlanAssignmentsActiveLabel-string", "Active")}
                </Text>
                <Text style={styles.sectionCount}>{activeAssignmentsWithPreview.length}</Text>
              </View>
            </View>

            {activeAssignmentsWithPreview.length === 0 ? (
              <View style={styles.inlineEmptyState}>
                <Text style={styles.inlineEmptyStateText}>
                  {t(
                    "nutritionPlanAssignmentsNoActivePlans-string",
                    "This athlete has no active nutrition plans.",
                  )}
                </Text>
              </View>
            ) : (
              <View style={styles.assignmentsGrid}>
                {activeAssignmentsWithPreview.map((assignment) => {
                  const isAssignmentPreview = assignment?.isAssignmentPreview === true;
                  const isPendingRestore = assignment?.isPendingRestore === true;
                  const actionKey = `archive:${assignment.client_plan_id}`;
                  const isPlanActionHovered = hoveredPlanActionKey === actionKey;

                  return (
                    <View
                      key={assignment.client_plan_id}
                      style={[
                        styles.assignmentTile,
                        isAssignmentPreview ? styles.assignmentTilePreview : null,
                        isPendingRestore ? styles.assignmentTilePendingRestore : null,
                      ]}
                    >
                      {!isAssignmentPreview ? (
                        <Tooltip
                          content="nutritionPlanAssignmentsArchiveTooltip-string"
                          placement="top"
                        >
                          <TouchableOpacity
                            activeOpacity={0.82}
                            onPress={() => stageAssignmentAction(assignment, "archive")}
                            disabled={isSaving}
                            style={[
                              styles.assignmentTileCornerAction,
                              styles.assignmentTileCornerActionArchive,
                            ]}
                            onMouseEnter={
                              !isMobile ? () => setHoveredPlanActionKey(actionKey) : undefined
                            }
                            onMouseLeave={() => setHoveredPlanActionKey(null)}
                          >
                            <View
                              style={[
                                styles.assignmentTileCornerActionInner,
                                isPlanActionHovered
                                  ? styles.assignmentTileCornerActionInnerArchiveHovered
                                  : null,
                              ]}
                            >
                              <View style={styles.assignmentTileCornerActionMinus} />
                            </View>
                          </TouchableOpacity>
                        </Tooltip>
                      ) : null}
                      <View style={styles.assignmentTileIconWrap}>
                        <View style={styles.assignmentTileIconBackground} />
                        <Ionicons
                          name="reader-outline"
                          size={32}
                          color={isAssignmentPreview ? Colors.darkBubbleColor : Colors.bubbleColor}
                        />
                        {isAssignmentPreview ? (
                          <View style={styles.newPill}>
                            <Text style={styles.newPillText}>
                              {t("nutritionPlanAssignmentsNewBadge-string", "NEW")}
                            </Text>
                          </View>
                        ) : null}
                      </View>

                      <Text
                        numberOfLines={2}
                        ellipsizeMode="tail"
                        adjustsFontSizeToFit={true}
                        minimumFontScale={0.82}
                        style={[styles.assignmentTileName, styles.assignmentTileNameActive]}
                      >
                        {getNutritionPlanDisplayName(assignment, language)}
                      </Text>
                      <View style={styles.assignmentTileSpacer} />
                    </View>
                  );
                })}
              </View>
            )}

            <TouchableOpacity
              activeOpacity={0.82}
              onPress={() => setIsArchivedExpanded((prev) => !prev)}
              style={styles.archivedToggle}
            >
              <View style={styles.archivedToggleLeft}>
                <Text style={styles.sectionTitle}>
                  {t(
                    "nutritionPlanAssignmentsLastArchivedSection-string",
                    "• Last archived",
                  )}
                </Text>
                <Text style={styles.sectionCount}>{archivedAssignments.length}</Text>
              </View>
              <Animated.View
                style={{ transform: [{ rotate: archivedChevronRotation }] }}
              >
                <Ionicons
                  name="chevron-down-outline"
                  size={14}
                  color={Colors.grayColor}
                />
              </Animated.View>
            </TouchableOpacity>

            {isArchivedExpanded ? (
              archivedAssignments.length === 0 ? (
                <View style={styles.archivedEmptyState}>
                  <Text style={styles.archivedEmptyStateText}>
                    {t(
                      "nutritionPlanAssignmentsNoArchivedPlans-string",
                      "No archived nutrition plans for this athlete.",
                    )}
                  </Text>
                </View>
              ) : (
                <View style={styles.assignmentsGrid}>
                  {archivedAssignments.map((assignment) => {
                    const actionKey = `restore:${assignment.client_plan_id}`;
                    const isPlanActionHovered = hoveredPlanActionKey === actionKey;
                    return (
                      <View
                        key={assignment.client_plan_id}
                        style={[
                          styles.assignmentTile,
                          styles.assignmentTileArchived,
                        ]}
                      >
                        <Tooltip
                          content="nutritionPlanAssignmentsRestoreTooltip-string"
                          placement="top"
                        >
                          <TouchableOpacity
                            activeOpacity={0.82}
                            onPress={() => stageAssignmentAction(assignment, "restore")}
                            disabled={isSaving}
                            style={[
                              styles.assignmentTileCornerAction,
                              styles.assignmentTileCornerActionRestore,
                            ]}
                            onMouseEnter={
                              !isMobile ? () => setHoveredPlanActionKey(actionKey) : undefined
                            }
                            onMouseLeave={() => setHoveredPlanActionKey(null)}
                          >
                            <View
                              style={[
                                styles.assignmentTileCornerActionInner,
                                styles.assignmentTileCornerActionInnerRestore,
                                isPlanActionHovered
                                  ? styles.assignmentTileCornerActionInnerRestoreHovered
                                  : null,
                              ]}
                            >
                              <Ionicons name="add" size={14} color={Colors.whiteColor} />
                            </View>
                          </TouchableOpacity>
                        </Tooltip>
                        <View style={styles.assignmentTileIconWrap}>
                          <View
                            style={[
                              styles.assignmentTileIconBackground,
                              styles.assignmentTileIconBackgroundArchived,
                            ]}
                          />
                          <Ionicons
                            name="reader-outline"
                            size={32}
                            color={Colors.darkgrayColor}
                          />
                        </View>

                        <Text
                          numberOfLines={2}
                          ellipsizeMode="tail"
                          adjustsFontSizeToFit={true}
                          minimumFontScale={0.82}
                          style={[styles.assignmentTileName, styles.assignmentTileNameArchived]}
                        >
                          {getNutritionPlanDisplayName(assignment, language)}
                        </Text>

                        <View style={styles.assignmentTileSpacer} />
                      </View>
                    );
                  })}
                </View>
              )
            ) : null}
          </>
        )}
      </ScrollView>
    </View>
  );

  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={isVisible}
      onRequestClose={handleClose}
    >
      <View style={GlobalStyles.centeredView}>
        <TouchableOpacity
          onPress={handleClose}
          style={GlobalStyles.touchableBackground}
          disabled={isBusy}
        >
          <View />
        </TouchableOpacity>
        <View style={[styles.modalPositioner, styles.modalPositionerVisibleViewport]}>
          <View
            style={[
              styles.modalView,
              styles.modalViewVisibleViewport,
              isCompactLayout
                ? [styles.modalViewCompact, styles.modalViewCompactVisibleViewport]
                : null,
            ]}
          >
            <View style={styles.header}>
              <View style={styles.headerText}>
                <Text numberOfLines={1} style={styles.title}>
                  {getNutritionPlanDisplayName(displayPlan, language)}
                </Text>
                <Text numberOfLines={2} style={styles.subtitle}>
                  {t(
                    "nutritionPlanAssignmentsSubtitle-string",
                    "Select athletes to assign this nutrition plan to.",
                  )}
                </Text>
              </View>
              <TouchableOpacity
                activeOpacity={0.8}
                onPress={handleClose}
                style={styles.closeButton}
                disabled={isBusy}
                onMouseEnter={!isMobile ? () => setIsCloseHovered(true) : undefined}
                onMouseLeave={!isMobile ? () => setIsCloseHovered(false) : undefined}
              >
                <Ionicons
                  name="close-outline"
                  size={24}
                  color={isCloseHovered ? Colors.darkgrayColor : Colors.grayColor}
                />
              </TouchableOpacity>
            </View>
            <View style={styles.headerDivider} />

            {isCompactLayout ? (
              <View style={[styles.body, styles.bodyCompact]}>
                {isCompactSelectionStep
                  ? renderSelectionPanel({ allowPanelScroll: true })
                  : renderCurrentPlansPanel({ allowPanelScroll: true })}
              </View>
            ) : (
              <View style={styles.body}>
                {renderSelectionPanel()}
                <View style={styles.bodyDivider} />
                {renderCurrentPlansPanel()}
              </View>
            )}

            <View style={styles.footer}>
              <View style={styles.footerActions}>
                <TouchableOpacity
                  activeOpacity={0.8}
                  onPress={isCompactReviewStep ? handleCompactBack : handleClose}
                  disabled={isBusy}
                  onMouseEnter={!isMobile ? () => setIsCancelHovered(true) : undefined}
                  onMouseLeave={() => setIsCancelHovered(false)}
                >
                  <View
                    style={[
                      styles.secondaryButton,
                      isCancelHovered ? styles.secondaryButtonHovered : null,
                    ]}
                  >
                    <Text style={styles.secondaryButtonText}>
                      {secondaryButtonLabel}
                    </Text>
                  </View>
                </TouchableOpacity>

                <TouchableOpacity
                  activeOpacity={0.86}
                  onPress={isCompactSelectionStep ? handleOpenCompactReview : handleSave}
                  disabled={isCompactSelectionStep ? selectedClientIds.length === 0 : !canSave}
                  onMouseEnter={!isMobile ? () => setIsSaveHovered(true) : undefined}
                  onMouseLeave={() => setIsSaveHovered(false)}
                >
                  <View
                    style={[
                      styles.primaryButton,
                      isCompactSelectionStep
                        ? selectedClientIds.length === 0
                          ? styles.primaryButtonDisabled
                          : null
                        : !canSave
                          ? styles.primaryButtonDisabled
                          : null,
                      (isCompactSelectionStep ? selectedClientIds.length > 0 : canSave) &&
                      isSaveHovered
                        ? styles.primaryButtonHovered
                        : null,
                    ]}
                  >
                    {!isCompactSelectionStep && isSaving ? (
                      <CustomActivityIndicator large={false} white={true} />
                    ) : (
                      <Text style={styles.primaryButtonText}>
                        {isCompactSelectionStep ? compactReviewButtonLabel : primaryButtonLabel}
                      </Text>
                    )}
                  </View>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
});

const styles = StyleSheet.create({
  modalPositioner: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100vh",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 20,
  },
  modalPositionerVisibleViewport: {
    height: "100dvh",
  },
  modalView: {
    width: "100%",
    maxWidth: 1120,
    height: 700,
    maxHeight: "90vh",
    backgroundColor: "#FEFEFD",
    borderRadius: 20,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 12,
    },
    shadowOpacity: 0.12,
    shadowRadius: 28,
    elevation: 10,
  },
  modalViewVisibleViewport: {
    maxHeight: "90dvh",
  },
  modalViewCompact: {
    maxWidth: 640,
    height: 700,
    maxHeight: "94vh",
  },
  modalViewCompactVisibleViewport: {
    maxHeight: "94dvh",
  },
  header: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    paddingHorizontal: 24,
    paddingTop: 22,
    paddingBottom: 18,
  },
  headerDivider: {
    height: 1,
    backgroundColor: Colors.borderColor,
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: 20,
    lineHeight: 26,
    fontWeight: "600",
    color: Colors.blackColor,
  },
  subtitle: {
    marginTop: 8,
    maxWidth: 760,
    fontSize: 13,
    lineHeight: 20,
    color: Colors.darkgrayColor,
  },
  closeButton: {
    width: 34,
    height: 34,
    alignItems: "center",
    justifyContent: "center",
  },
  body: {
    flex: 1,
    flexDirection: "row",
    alignItems: "stretch",
    minHeight: 0,
  },
  bodyCompact: {
    flex: 1,
    flexDirection: "column",
    minHeight: 0,
  },
  panel: {
    flexDirection: "column",
    minWidth: 0,
    minHeight: 0,
  },
  bodyDivider: {
    width: 1,
    alignSelf: "stretch",
    backgroundColor: Colors.borderColor,
  },
  currentPlansPanel: {
    flex: 1.05,
    minHeight: 0,
  },
  selectionPanel: {
    flex: 0.95,
    alignSelf: "stretch",
    overflow: "hidden",
    minHeight: 0,
  },
  panelCompact: {
    width: "100%",
  },
  panelCompactStep: {
    flex: 1,
  },
  panelTopBlock: {
    width: "100%",
  },
  panelTopBlockCompact: {
    paddingTop: 18,
    paddingHorizontal: 24,
  },
  selectionPanelTopBlockDesktop: {
    paddingTop: 8,
    paddingLeft: 24,
    paddingRight: 18,
  },
  selectionPanelHeaderDesktop: {
    marginTop: 8,
  },
  panelHeader: {
    marginBottom: 14,
  },
  panelEyebrow: {
    fontSize: 10,
    lineHeight: 14,
    color: Colors.grayColor,
    textTransform: "uppercase",
    letterSpacing: 0.6,
  },
  panelTitle: {
    marginTop: 4,
    fontSize: 16,
    lineHeight: 22,
    fontWeight: "500",
    color: Colors.blackColor,
  },
  searchField: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    minHeight: 42,
    marginBottom: 16,
    paddingHorizontal: 12,
    borderRadius: 12,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
    backgroundColor: "transparent",
    transitionProperty: "border-color, background-color",
    transitionDuration: "180ms",
    transitionTimingFunction: "ease-in-out",
  },
  searchFieldHovered: {
    borderColor: Colors.grayColor,
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    fontSize: 14,
    color: Colors.blackColor,
    outlineStyle: "none",
  },
  searchInputIOS: {
    fontSize: 16,
  },
  selectedClientList: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    paddingBottom: 4,
    marginBottom: 14,
  },
  selectedClientListCompact: {
    flexDirection: "column",
    flexWrap: "nowrap",
  },
  selectedClientRow: {
    position: "relative",
    minWidth: 148,
    flexGrow: 1,
    flexBasis: 148,
    maxWidth: 188,
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 12,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
    transitionProperty: "border-color, background-color",
    transitionDuration: "180ms",
    transitionTimingFunction: "ease-in-out",
  },
  selectedClientRowCompact: {
    minWidth: 0,
    width: "100%",
    alignSelf: "stretch",
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: "auto",
    maxWidth: "100%",
  },
  selectedClientRowConflict: {
    borderColor: "rgba(194, 65, 12, 0.4)",
    backgroundColor: Colors.warningBackgroundColor,
  },
  selectedClientRowHovered: {
    borderColor: Colors.grayColor,
    backgroundColor: Colors.hoveredGray,
  },
  selectedClientRowFocused: {
    borderColor: Colors.bubbleColor,
    backgroundColor: "rgba(63, 123, 141, 0.04)",
  },
  selectedClientRowFocusedHovered: {
    borderColor: Colors.bubbleColor,
    backgroundColor: "rgba(63, 123, 141, 0.08)",
  },
  selectedClientMainButton: {
    flex: 1,
    minWidth: 0,
  },
  selectedClientIdentity: {
    paddingVertical: 12,
    paddingLeft: 12,
    paddingRight: 28,
  },
  selectedClientIdentityContent: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 6,
  },
  selectedClientNameRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    minWidth: 0,
    width: "100%",
  },
  selectedClientName: {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    lineHeight: 18,
    color: Colors.darkgrayColor,
    fontWeight: "500",
  },
  selectedClientNameFocused: {
    color: Colors.bubbleColor,
  },
  selectedClientConflictText: {
    fontSize: 11,
    lineHeight: 15,
    color: Colors.warningTextColor,
  },
  selectedClientRemoveButton: {
    position: "absolute",
    top: 6,
    right: 6,
    width: 22,
    height: 22,
    alignItems: "center",
    justifyContent: "center",
    transitionProperty: "color",
    transitionDuration: "180ms",
    transitionTimingFunction: "ease-in-out",
  },
  selectedClientBadgeRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    minHeight: 18,
  },
  selectedClientBadgeRowPlaceholder: {
    minHeight: 18,
  },
  selectedClientDeltaBadge: {
    minWidth: 24,
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 999,
    alignItems: "center",
    justifyContent: "center",
  },
  selectedClientDeltaBadgeAdd: {
    backgroundColor: "rgba(63, 123, 141, 0.12)",
  },
  selectedClientDeltaBadgeRemove: {
    backgroundColor: "rgba(204, 74, 74, 0.12)",
  },
  selectedClientDeltaBadgeText: {
    fontSize: 10,
    lineHeight: 12,
    color: Colors.bubbleColor,
    fontWeight: "700",
  },
  selectedClientDeltaBadgeTextRemove: {
    color: Colors.redColor,
  },
  sectionHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginTop: 14,
    marginBottom: 10,
  },
  sectionHeaderLead: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  sectionTitle: {
    fontSize: 11,
    lineHeight: 16,
    color: Colors.grayColor,
    letterSpacing: 0.45,
    textTransform: "uppercase",
  },
  sectionCount: {
    minWidth: 20,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: "rgba(0,0,0,0.04)",
    textAlign: "center",
    fontSize: 10,
    lineHeight: 14,
    color: Colors.darkgrayColor,
  },
  panelScroll: {
    flex: 1,
    minHeight: 0,
    width: "100%",
    alignSelf: "stretch",
  },
  panelScrollDesktop: {
    overscrollBehavior: "contain",
  },
  panelScrollCompact: {
    flex: 1,
    flexGrow: 1,
    flexShrink: 1,
    minHeight: 0,
  },
  selectionPanelScroll: {
    flex: 1,
    minHeight: 0,
  },
  panelScrollContent: {
    paddingBottom: 0,
  },
  selectionPanelScrollContentCompact: {
    paddingLeft: 24,
    paddingRight: 24,
  },
  selectionPanelScrollContentDesktop: {
    paddingLeft: 24,
    paddingRight: 18,
    paddingBottom: 8,
  },
  currentPlansPanelScrollContentCompact: {
    paddingLeft: 24,
    paddingRight: 24,
    paddingTop: 18,
    paddingBottom: 18,
  },
  currentPlansPanelScrollContentDesktop: {
    paddingTop: 8,
    paddingLeft: 18,
    paddingRight: 24,
    paddingBottom: 8,
  },
  clientRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 11,
    borderRadius: 12,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
    marginBottom: 8,
    transitionProperty: "border-color, background-color",
    transitionDuration: "180ms",
    transitionTimingFunction: "ease-in-out",
  },
  clientRowSelected: {
    borderColor: Colors.bubbleColor,
    backgroundColor: "rgba(63, 123, 141, 0.06)",
  },
  clientRowHovered: {
    borderColor: Colors.grayColor,
    backgroundColor: Colors.hoveredGray,
  },
  clientRowSelectedHovered: {
    borderColor: Colors.bubbleColor,
    backgroundColor: "rgba(63, 123, 141, 0.1)",
  },
  clientRowCheckbox: {
    width: 24,
    alignItems: "center",
  },
  checkbox: {
    width: 16,
    height: 16,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: Colors.whiteColor,
  },
  checkboxSelected: {
    borderColor: Colors.bubbleColor,
    backgroundColor: Colors.bubbleColor,
  },
  clientRowText: {
    flex: 1,
    minWidth: 0,
    marginLeft: 10,
  },
  clientNameRow: {
    flexDirection: "row",
    alignItems: "center",
    minWidth: 0,
    gap: 6,
  },
  clientName: {
    fontSize: 13,
    lineHeight: 18,
    color: Colors.darkgrayColor,
    flexShrink: 1,
  },
  clientConflictIconWrap: {
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  conflictCallout: {
    marginBottom: 6,
    padding: 14,
    borderRadius: 12,
    borderWidth: 0.5,
    borderColor: "rgba(194, 65, 12, 0.28)",
    backgroundColor: Colors.warningBackgroundColor,
    gap: 12,
  },
  conflictCalloutHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  conflictCalloutTitleWrap: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  conflictCalloutTitle: {
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "600",
    color: Colors.warningTextColor,
  },
  conflictReasonList: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  conflictReasonChip: {
    paddingHorizontal: 9,
    paddingVertical: 5,
    borderRadius: 999,
    backgroundColor: "rgba(255,255,255,0.72)",
  },
  conflictReasonChipText: {
    fontSize: 11,
    lineHeight: 14,
    color: Colors.warningTextColor,
    fontWeight: "500",
  },
  conflictCalloutActions: {
    width: "100%",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    gap: 8,
  },
  conflictAcceptButtonHover: {
    borderColor: "rgba(194, 65, 12, 0.42)",
    backgroundColor: "rgba(255,255,255,0.96)",
  },
  conflictAcceptButton: {
    minHeight: 32,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 10,
    backgroundColor: Colors.whiteColor,
    borderWidth: 0.5,
    borderColor: "rgba(194, 65, 12, 0.24)",
    alignItems: "center",
    justifyContent: "center",
  },
  conflictAcceptButtonText: {
    fontSize: 12,
    lineHeight: 16,
    color: Colors.warningTextColor,
    fontWeight: "600",
  },
  conflictRemoveButtonHover: {
    backgroundColor: "rgba(255,255,255,0.36)",
  },
  conflictRemoveButton: {
    minHeight: 32,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  conflictRemoveButtonText: {
    fontSize: 12,
    lineHeight: 16,
    color: Colors.darkgrayColor,
    fontWeight: "500",
  },
  assignmentsGrid: {
    width: "100%",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    alignContent: "flex-start",
  },
  assignmentTile: {
    width: 116,
    minHeight: 144,
    paddingHorizontal: 6,
    paddingTop: 6,
    paddingBottom: 6,
    alignItems: "center",
    position: "relative",
    borderRadius: 12,
  },
  assignmentTileArchived: {
    backgroundColor: "transparent",
    borderRadius: 0,
  },
  assignmentTilePreview: {},
  assignmentTilePendingArchive: {
    backgroundColor: "rgba(204, 74, 74, 0.08)",
    borderWidth: 0.5,
    borderColor: "rgba(204, 74, 74, 0.22)",
  },
  assignmentTilePendingRestore: {
    backgroundColor: "transparent",
    borderWidth: 0,
    borderColor: "transparent",
    borderRadius: 0,
  },
  assignmentTileCornerAction: {
    position: "absolute",
    top: 8,
    right: 26,
    zIndex: 3,
    width: 22,
    height: 22,
    alignItems: "center",
    justifyContent: "center",
  },
  assignmentTileCornerActionArchive: {},
  assignmentTileCornerActionRestore: {},
  assignmentTileCornerActionInner: {
    width: 20,
    height: 20,
    borderRadius: 999,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: Colors.redColor,
    transitionProperty: "background-color, opacity",
    transitionDuration: "180ms",
    transitionTimingFunction: "ease-in-out",
  },
  assignmentTileCornerActionInnerRestore: {
    backgroundColor: Colors.bubbleColor,
  },
  assignmentTileCornerActionInnerArchiveHovered: {
    opacity: 0.88,
  },
  assignmentTileCornerActionInnerRestoreHovered: {
    backgroundColor: Colors.darkBubbleColor,
  },
  assignmentTileCornerActionMinus: {
    width: 8,
    height: 2,
    borderRadius: 999,
    backgroundColor: Colors.whiteColor,
  },
  assignmentTileIconWrap: {
    width: "100%",
    height: 68,
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  assignmentTileIconBackground: {
    position: "absolute",
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: "rgba(63, 123, 141, 0.08)",
  },
  assignmentTileIconBackgroundArchived: {
    backgroundColor: "rgba(75, 85, 99, 0.08)",
  },
  assignmentTileName: {
    width: "100%",
    minHeight: 34,
    marginTop: 0,
    fontSize: 12,
    lineHeight: 17,
    color: Colors.blackColor,
    fontWeight: "500",
    textAlign: "center",
  },
  assignmentTileNameActive: {
    fontSize: 12,
    lineHeight: 17,
    minHeight: 34,
  },
  assignmentTileNameArchived: {
    color: Colors.darkgrayColor,
  },
  newPill: {
    position: "absolute",
    top: 7,
    right: 12,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: "rgba(255, 255, 255, 0.82)",
    borderWidth: 1,
    borderColor: "rgba(46, 94, 110, 0.18)",
    shadowColor: Colors.darkBubbleColor,
    shadowOpacity: 0.08,
    shadowRadius: 6,
    shadowOffset: {
      width: 0,
      height: 2,
    },
    transform: [{ rotate: "-6deg" }],
  },
  newPillText: {
    fontSize: 8,
    fontWeight: "800",
    letterSpacing: 1.3,
    textTransform: "uppercase",
    color: Colors.darkBubbleColor,
  },
  assignmentTileSpacer: {
    width: "100%",
    height: 18,
  },
  archivedToggle: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    paddingTop: 6,
    paddingBottom: 10,
  },
  archivedToggleLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  footer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    flexShrink: 0,
    borderTopWidth: 0.5,
    borderTopColor: Colors.borderColor,
    paddingHorizontal: 24,
    paddingVertical: 16,
  },
  footerActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  secondaryButton: {
    minWidth: 96,
    minHeight: 38,
    paddingHorizontal: 16,
    paddingVertical: 9,
    borderRadius: 12,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: Colors.whiteColor,
    transitionProperty: "background-color",
    transitionDuration: "180ms",
    transitionTimingFunction: "ease-in-out",
  },
  secondaryButtonHovered: {
    backgroundColor: Colors.backgroundColor,
  },
  secondaryButtonText: {
    ...Fonts.medium13,
    color: Colors.darkgrayColor,
  },
  primaryButton: {
    minHeight: 40,
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: Colors.bubbleColor,
    transitionProperty: "opacity",
    transitionDuration: "180ms",
    transitionTimingFunction: "ease-in-out",
  },
  primaryButtonHovered: {
    opacity: 0.9,
  },
  primaryButtonDisabled: {
    opacity: 0.48,
  },
  primaryButtonText: {
    ...Fonts.medium13,
    color: Colors.whiteColor,
  },
  emptyState: {
    flex: 1,
    minHeight: 220,
    paddingHorizontal: 6,
  },
  emptyStateTitle: {
    marginTop: 12,
    fontSize: 14,
    lineHeight: 20,
    color: Colors.blackColor,
    fontWeight: "500",
  },
  emptyStateCopy: {
    marginTop: 6,
    maxWidth: 360,
    textAlign: "center",
    fontSize: 12,
    lineHeight: 18,
    color: Colors.grayColor,
  },
  inlineEmptyState: {
    paddingHorizontal: 12,
    paddingVertical: 14,
    borderRadius: 12,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
    backgroundColor: "#FCFCFB",
    marginBottom: 8,
  },
  inlineEmptyStateText: {
    fontSize: 12,
    lineHeight: 18,
    color: Colors.grayColor,
  },
  searchEmptyState: {
    width: "100%",
    marginTop: 72,
    paddingHorizontal: 24,
    alignItems: "center",
    justifyContent: "center",
  },
  searchEmptyStateText: {
    maxWidth: 320,
    textAlign: "center",
    fontSize: 12,
    lineHeight: 18,
    color: Colors.grayColor,
  },
  archivedEmptyState: {
    width: "100%",
    paddingVertical: 24,
    alignItems: "center",
    justifyContent: "center",
  },
  archivedEmptyStateText: {
    maxWidth: 320,
    textAlign: "center",
    fontSize: 12,
    lineHeight: 18,
    color: Colors.grayColor,
  },
  loadingState: {
    flex: 1,
    minHeight: 220,
    alignItems: "center",
    justifyContent: "center",
  },
});
